import type { Nullable } from 'common/types'

export const isDefined = <T>(value: T): value is NonNullable<T> => value != null
export function assertToBeDefined<T>(value: T): asserts value is NonNullable<T> {
  if (isDefined(value)) return

  throw new Error('Got null | undefined where value expected')
}

export const isObject = (value: unknown): value is object =>
  typeof value === 'object' && isDefined(value)

export const isNumber = (value: unknown): value is number => typeof value === 'number'

export const isOfType =
  <TargetType>(boundary: readonly TargetType[]) =>
  (toTest: unknown): toTest is TargetType =>
    boundary.some(value => value === toTest)

export const hasKeys = <Keys extends string[], Values>(
  object: Record<PropertyKey, Values>,
  ...keys: Keys
): object is Record<PropertyKey, Values> & Record<Keys[number], Values> =>
  keys.every(key => key in object)

export const hasKeysOfUnknown = <Keys extends string>(
  obj: object,
  ...keys: Keys[]
): obj is object & Record<Keys, unknown> => keys.every(key => key in obj)

export const hasKeysExact = <Keys extends string>(
  obj: object,
  ...keys: Keys[]
): obj is Record<Keys, unknown> => {
  const toTestKeys = new Set<string>(keys)
  return Object.keys(obj).every(key => toTestKeys.has(key))
}

export function assertToBeObject(value: unknown): asserts value is object {
  if (isObject(value)) return

  throw new Error(`Got ${JSON.stringify(value)} where object expected`)
}

export function assert(condition: boolean, message = 'Condition is false'): asserts condition {
  if (condition) return

  throw new Error(message)
}

export function safeParse<T>(
  value: string,
  validator: (candidate: unknown) => candidate is T,
  message = 'Не тот объект',
): T {
  const parsed: unknown = JSON.parse(value)
  assert(validator(parsed), message)

  return parsed
}

export function safeParseOrNullify<T>(
  value: string,
  validator: (candidate: unknown) => candidate is T,
): Nullable<T> {
  const parsed: unknown = JSON.parse(value)

  if (!validator(parsed)) return null

  return parsed
}
